/**
 * Utility functions for compliance scoring
 * Shared between ConversationDetailsCX and ConversationDetailsNative
 */

import { toast } from 'react-toastify';
import { gradeForScore } from './complianceGrades';

/**
 * Scorecard feature flag (backend `features.scorecard`, defaulted in api/Index.js).
 * On  -> the whole surface is presented as the "Scorecard".
 * Off -> unchanged "Compliance" wording.
 *
 * Single source of truth for the copy so the header, the refresh button and the
 * criteria-source caption can't drift apart.
 *
 * @param {Object} apiClient - API client instance
 * @returns {boolean}
 */
export const isScorecardEnabled = (apiClient) =>
  Boolean(apiClient?.features?.scorecard);

/**
 * Noun for the score surface: "Scorecard" or "Compliance".
 */
export const getScoreSurfaceLabel = (apiClient) =>
  isScorecardEnabled(apiClient) ? 'Scorecard' : 'Compliance';

/**
 * Copy for the manual refresh button.
 */
export const getScoreRefreshLabel = (apiClient) =>
  isScorecardEnabled(apiClient)
    ? 'Update my Scorecard'
    : 'Update My Compliance Score';

/**
 * Copy for the per-visit authorship tag (COM-390 "Score All Notes"). A visit's
 * `authorship` field ('comprehend' | 'clinician' | 'mixed') says whether the
 * note was written through Comprehend's AI, entirely by the clinician, or a
 * mix of both. Single source of truth so this copy can't drift between the
 * per-visit badge and any future surface that shows it (e.g. exports).
 *
 * Deliberately not tied to isScorecardEnabled — authorship is orthogonal to
 * the Scorecard/Compliance relabel and always uses the same words.
 *
 * @param {Object} apiClient - API client instance (unused today, accepted for
 *   signature parity with the other label helpers here in case authorship
 *   copy ever needs to vary per feature flag).
 * @returns {{aiAuthored: string, clinicianAuthored: string, mixed: string}}
 */
export const getAuthorshipLabels = (apiClient) => ({
  aiAuthored: 'AI-authored',
  clinicianAuthored: 'Clinician-authored',
  mixed: 'Mixed',
});

/**
 * Extract only "Existing Value" fields, remove "Description" fields, and unwrap "Existing Value" keys
 * This function recursively processes nested objects to clean up EMR data structures
 *
 * @param {any} obj - The object to process
 * @returns {any} - The cleaned object with only existing values
 */
export const extractExistingValuesOnly = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(extractExistingValuesOnly);

  // If object has "Existing Value", unwrap it recursively
  if ('Existing Value' in obj) {
    return extractExistingValuesOnly(obj['Existing Value']);
  }

  // Build result object, skipping "Description" and recursively processing all values
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key === 'Description') continue;
    result[key] = extractExistingValuesOnly(value);
  }
  return result;
};

/**
 * Handle getting compliance score with existing values
 * This is a generic function that accepts a callback to get existing values
 *
 * @param {Object} params - Parameters object
 * @param {number} params.conversationID - The conversation ID
 * @param {boolean} params.complianceScoreLoading - Current loading state
 * @param {Function} params.setComplianceScoreLoading - Setter for loading state
 * @param {Object} params.apiClient - API client instance
 * @param {Function} params.getExistingValuesMapping - Async function that returns existing values mapping
 * @param {Function} params.getEmrMetadata - Optional async function returning the EMR's note metadata
 *   (CORA Sapphire's noteMetadata blob). Its isMC / isWC / isPeds flags decide which
 *   compliance rule set grades the note. Omit on surfaces with no EMR attached.
 * @param {Function} params._setComplianceScore - Setter for compliance score
 * @param {Function} params._setComplianceFeedback - Setter for compliance feedback
 * @param {Function} params._setComplianceCriteriaSource - Setter for compliance criteria source (optional)
 * @param {Function} params._setStatus - Setter for status message
 * @returns {Promise<void>}
 */
export const handleGetComplianceScore = async ({
  conversationID,
  complianceScoreLoading,
  setComplianceScoreLoading,
  apiClient,
  getExistingValuesMapping,
  getEmrMetadata = null,
  _setComplianceScore,
  _setComplianceFeedback,
  _setComplianceCriteriaSource,
  _setStatus,
}) => {
  if (!conversationID || complianceScoreLoading) return;

  setComplianceScoreLoading(true);
  _setComplianceScore(0); // Clear stale score so old grade doesn't persist during fetch

  const applyScoreData = (
    score,
    feedback,
    requirement_evaluations,
    criteria_source,
  ) => {
    _setComplianceScore(score);
    const feedbackToUse = requirement_evaluations || feedback || [];
    _setComplianceFeedback(feedbackToUse);
    if (_setComplianceCriteriaSource) {
      _setComplianceCriteriaSource(criteria_source || 'cms');
    }
    const grade = score === null || score === undefined || Number.isNaN(Number(score))
      ? '--'
      : gradeForScore(score);
    _setStatus(`✅ Compliance Score: ${grade}`);
  };

  try {
    // Step 1: Get existing values mapping (implementation differs for CX vs Native)
    const existingValuesMapping = await getExistingValuesMapping();
    console.log('Existing values mapping:', existingValuesMapping);

    // Step 1b: Read the EMR's note metadata, if this EMR publishes any. It picks
    // the compliance rule set (Medicare / Work Comp / Peds) server side, so a
    // failure here must not block scoring -- we just grade against the default.
    let emrMetadata = null;
    if (typeof getEmrMetadata === 'function') {
      try {
        emrMetadata = await getEmrMetadata();
      } catch (metadataErr) {
        console.log('Could not read EMR note metadata:', metadataErr);
      }
    }

    // Step 2: Call the score endpoint with a 28s timeout
    let response;
    try {
      response = await Promise.race([
        apiClient.scoreConversation(
          conversationID,
          existingValuesMapping,
          emrMetadata,
        ),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('SCORE_TIMEOUT')), 28000),
        ),
      ]);
    } catch (raceErr) {
      if (raceErr.message === 'SCORE_TIMEOUT') {
        // Score endpoint timed out — poll conversation details for the saved score
        for (let i = 0; i < 24; i++) {
          await new Promise((r) => setTimeout(r, 5000));
          const data = await apiClient.getConversationDetails(conversationID);
          if (data?.score) {
            applyScoreData(
              data.score,
              data.feedback,
              data.requirement_evaluations,
              data.criteria_source,
            );
            return;
          }
        }
        _setStatus('❌ Compliance score unavailable');
        return;
      }
      throw raceErr;
    }

    // Handle API surface errors explicitly so users see the real failure reason
    if (response?.error) {
      const rawError = response.error;
      const errorMessage =
        rawError?.message ||
        rawError?.error ||
        rawError?.detail ||
        rawError ||
        'Failed to get compliance score';
      toast.error(errorMessage);
      _setStatus(`❌ ${errorMessage}`);
      return;
    }

    // Step 3: Update state with results
    if (response && response.answer) {
      const { score, feedback, requirement_evaluations, criteria_source } =
        response.answer;
      applyScoreData(score, feedback, requirement_evaluations, criteria_source);
    } else {
      const fallbackMessage = 'Failed to get compliance score';
      toast.error(fallbackMessage);
      _setStatus(`❌ ${fallbackMessage}`);
    }
  } catch (error) {
    console.error('Error getting compliance score:', error);
    const errorMessage =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      'Error getting compliance score. Please try again.';
    toast.error(errorMessage);
    _setStatus(`❌ ${errorMessage}`);
  } finally {
    setComplianceScoreLoading(false);
  }
};
